import { createFrontendModule } from '@backstage/frontend-plugin-api';
import { EntityCardBlueprint } from '@backstage/plugin-catalog-react/alpha';
import codeInsightsPlugin from '@roadiehq/backstage-plugin-github-insights/alpha';
import {
  ContributorsCard,
  LanguagesCard,
  ReadMeCard,
  ReleasesCard,
} from '@roadiehq/backstage-plugin-github-insights';
import { GithubSignInCard } from './GithubSignInCard';
import { GithubSignInGate } from './GithubSignInGate';
import { ReleaseRows } from './ReleaseRows';

/*
 * Collapse the code-insights cards' four sign-in prompts into one.
 *
 * Each card renders its own not-authorized `InfoCard` when the GitHub scope is
 * missing, so a signed-out Repository overview stacks four copies of the same
 * sentence and blue-outline button — the Readme's inside a second nested card.
 * Gating the cards and adding one prompt is the structural half of the entity
 * page pass; see `GithubSignInCard`.
 *
 * The gate wraps the loader rather than replacing the card, so the card keeps
 * its own filter, config and placement from app-config.
 */
const readmeCard = codeInsightsPlugin
  .getExtension('entity-card:code-insights/readme')
  .override({
    factory(originalFactory, { config }) {
      return originalFactory({
        params: {
          loader: async () => (
            <GithubSignInGate>
              <ReadMeCard maxHeight={config.maxHeight} title={config.title} />
            </GithubSignInGate>
          ),
        },
      });
    },
  });

const languagesCard = codeInsightsPlugin
  .getExtension('entity-card:code-insights/languages')
  .override({
    params: {
      loader: async () => (
        <GithubSignInGate>
          <LanguagesCard />
        </GithubSignInGate>
      ),
    },
  });

const releasesCard = codeInsightsPlugin
  .getExtension('entity-card:code-insights/releases')
  .override({
    params: {
      loader: async () => (
        <GithubSignInGate>
          <ReleaseRows>
            <ReleasesCard />
          </ReleaseRows>
        </GithubSignInGate>
      ),
    },
  });

const contributorsCard = codeInsightsPlugin
  .getExtension('entity-card:code-insights/contributors')
  .override({
    params: {
      loader: async () => (
        <GithubSignInGate>
          <ContributorsCard />
        </GithubSignInGate>
      ),
    },
  });

/*
 * Matches the kind filter app-config puts on the four cards above, so the
 * prompt appears exactly where they would have.
 */
const signInCard = EntityCardBlueprint.make({
  name: 'code-insights-sign-in',
  params: {
    filter: 'kind:repository',
    type: 'content',
    loader: async () => <GithubSignInCard />,
  },
});

export const codeInsightsCardsModule = createFrontendModule({
  pluginId: 'code-insights',
  extensions: [
    readmeCard,
    languagesCard,
    releasesCard,
    contributorsCard,
    signInCard,
  ],
});
